import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, updateDoc, setDoc, collection, query, where, orderBy, limit, getDocs, onSnapshot, addDoc, getDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import { useMijnVerhaalData, type CarouselCard } from '../../hooks/useMijnVerhaalData';
import { LinkPreviewCard } from '../LinkPreviewCard';
import type { LinkPreview } from '../../services/linkPreviewService';

// Helper function to format time ago
const formatTimeAgo = (date: Date): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} maanden geleden`;
  return `${Math.floor(diffDays / 365)} jaar geleden`;
};

// Image compression utility using Canvas API
// Preserves PNG format if original is PNG (important for logos with transparency)
const compressImage = (file: File, maxWidth: number = 1920, quality: number = 0.8, preserveFormat: boolean = false): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // Determine output format: preserve PNG if original is PNG and preserveFormat is true
        const isPNG = file.type === 'image/png';
        const outputFormat = (preserveFormat && isPNG) ? 'image/png' : 'image/jpeg';
        const outputExtension = (preserveFormat && isPNG) ? '.png' : '.jpg';
        
        // Convert to blob - PNG doesn't use quality parameter, JPEG does
        const blobCallback = (blob: Blob | null) => {
          if (!blob) {
            reject(new Error('Could not compress image'));
            return;
          }
          
          // Create new file from blob
          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, outputExtension), {
            type: outputFormat,
            lastModified: Date.now(),
          });
          
          console.log(`📸 Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB (${outputFormat})`);
          resolve(compressedFile);
        };
        
        // Call toBlob with or without quality parameter depending on format
        if (outputFormat === 'image/png') {
          canvas.toBlob(blobCallback, outputFormat);
        } else {
          canvas.toBlob(blobCallback, outputFormat, quality);
        }
      };
      
      img.onerror = () => reject(new Error('Could not load image'));
    };
    
    reader.onerror = () => reject(new Error('Could not read file'));
  });
};

// Inline Editable Text Component
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  isOwner: boolean;
  className?: string;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
}> = ({ value, onSave, isOwner, className = '', placeholder = 'Klik om te bewerken...', multiline = false, maxLength }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (!isOwner) {
    return <span className={className}>{value || placeholder}</span>;
  }

  if (isEditing) {
    const commonProps = {
      value: editValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEditValue(e.target.value),
      onBlur: handleSave,
      onKeyDown: handleKeyDown,
      maxLength,
      className: `${className} bg-white/20 backdrop-blur-sm border border-white/30 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-[#4F6660]/50`,
    };

    return multiline ? (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        {...commonProps}
        rows={3}
      />
    ) : (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        {...commonProps}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className={`${className} cursor-pointer hover:bg-white/10 rounded px-1 -mx-1 transition-colors group relative`}
      title="Klik om te bewerken"
    >
      {value || <span className="opacity-50 italic">{placeholder}</span>}
      <span className="absolute -right-5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-xs">✏️</span>
    </span>
  );
};

// Social Link Input Component with local state (saves on blur) and visibility toggle
const SocialLinkInput: React.FC<{
  fieldKey: string;
  label: string;
  placeholder: string;
  value: string;
  isVisible: boolean;
  onSave: (value: string) => void;
  onToggleVisibility: (visible: boolean) => void;
}> = ({ label, placeholder, value, isVisible, onSave, onToggleVisibility }) => {
  const [localValue, setLocalValue] = useState(value);
  
  // Sync with external value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);
  
  const handleBlur = () => {
    if (localValue !== value) {
      onSave(localValue);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      {/* Visibility toggle */}
      <button
        type="button"
        onClick={() => onToggleVisibility(!isVisible)}
        className={`w-5 h-5 rounded flex items-center justify-center text-xs transition-all ${
          isVisible 
            ? 'text-white' 
            : 'bg-white/10 text-gray-500 hover:bg-white/20'
        }`}
        style={isVisible ? { backgroundColor: 'var(--accent-color, #4F6660)' } : undefined}
        title={isVisible ? 'Verbergen voor bezoekers' : 'Tonen aan bezoekers'}
      >
        {isVisible ? '👁' : ''}
      </button>
      <span className="text-sm w-6">{label}</span>
      <input
        type="url"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`flex-1 px-2 py-1 text-xs bg-white/10 border border-white/20 rounded text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-1 ${
          !isVisible ? 'opacity-50' : ''
        }`}
        style={{ 
          '--tw-ring-color': 'var(--accent-color, #4F6660)',
        } as React.CSSProperties}
      />
    </div>
  );
};

interface ProfileSliderHomeFromVerhaalProps {
  selectedUserId?: string | null;
  onBack?: () => void;
}

const ProfileSliderHomeFromVerhaal: React.FC<ProfileSliderHomeFromVerhaalProps> = ({ selectedUserId, onBack }) => {
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;
  const isOwnProfile = !selectedUserId || selectedUserId === currentUserId;
  
  const { userProfile, carouselCards, loading, error } = useMijnVerhaalData(selectedUserId);
  
  // Hero background state
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  
  // Connect modal state
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectMessage, setConnectMessage] = useState('');
  const [requestSent, setRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);

  // Recommend modal state
  const [showRecommendModal, setShowRecommendModal] = useState(false);
  const [recommendMessage, setRecommendMessage] = useState('');
  const [selectedRecommendTo, setSelectedRecommendTo] = useState<string>('');
  const [postToNewsfeed, setPostToNewsfeed] = useState(true);
  const [sendingRecommendation, setSendingRecommendation] = useState(false);
  const [recommendationSent, setRecommendationSent] = useState(false);
  const [userConnections, setUserConnections] = useState<Array<{id: string, name: string, photoURL?: string}>>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  // Inline editing states
  const [saving, setSaving] = useState(false);
  const [showAddExpertise, setShowAddExpertise] = useState(false);
  const [newExpertiseValue, setNewExpertiseValue] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showImagePositioner, setShowImagePositioner] = useState(false);
  const [tempPhotoURL, setTempPhotoURL] = useState<string>('');
  const [tempPhotoFile, setTempPhotoFile] = useState<File | null>(null);
  const [tempPhotoPosition, setTempPhotoPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  
  // Preview mode - view profile as others see it
  const [previewMode, setPreviewMode] = useState(false);
  const effectiveIsOwner = isOwnProfile && !previewMode;

  // Local state for optimistic updates (prevents flickering)
  const [localUpdates, setLocalUpdates] = useState<Record<string, any>>({});

  // Merged profile with local updates
  const effectiveProfile = useMemo(() => {
    if (!userProfile) return null;
    try {
      return { ...userProfile, ...localUpdates };
    } catch (err) {
      console.error('Error creating effectiveProfile:', err);
      return userProfile;
    }
  }, [userProfile, localUpdates]);

  // Suggesties voor kernwaarden
  const coreValueOptions = [
    'Vertrouwen',
    'Respect',
    'Communicatie',
    'Integriteit',
    'Transparantie',
    'Samenwerken',
    'Groei',
    'Impact',
    'Creativiteit',
    'Service',
    'Betrouwbaarheid',
    'Eerlijkheid',
  ];

  // Save field to Firebase with optimistic update
  const saveField = useCallback(async (field: string, value: any) => {
    if (!currentUserId) return;
    
    // Optimistic update - update local state immediately
    setLocalUpdates(prev => ({ ...prev, [field]: value }));
    
    setSaving(true);
    try {
      const db = getFirestore();
      const userDocRef = doc(db, 'userProfiles', currentUserId);
      await updateDoc(userDocRef, { [field]: value });
      console.log(`✅ Saved ${field}:`, value);
      // Don't refetch - we already have the updated value locally
    } catch (err: any) {
      // If doc doesn't exist, create it
      if (err.code === 'not-found') {
        const db = getFirestore();
        const userDocRef = doc(db, 'userProfiles', currentUserId);
        await setDoc(userDocRef, { [field]: value }, { merge: true });
      } else {
        console.error('❌ Error saving:', err);
        // Revert optimistic update on error
        setLocalUpdates(prev => {
          const newState = { ...prev };
          delete newState[field];
          return newState;
        });
        alert('Opslaan mislukt. Probeer opnieuw.');
      }
    } finally {
      setSaving(false);
    }
  }, [currentUserId]);

  // Save card data to Firebase (no refetch to prevent flickering)
  const saveCardField = useCallback(async (cardId: string, field: string, value: any) => {
    if (!currentUserId) return;
    
    setSaving(true);
    try {
      const db = getFirestore();
      
      // Find the card in carouselCards collection by userId
      const cardsQuery = query(
        collection(db, 'carouselCards'),
        where('userId', '==', currentUserId)
      );
      const cardsSnapshot = await getDocs(cardsQuery);
      
      // Find the card with matching id
      const cardDoc = cardsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.id === cardId;
      });
      
      if (!cardDoc) {
        // Card doesn't exist, create it
        const newCardRef = doc(collection(db, 'carouselCards'));
        await setDoc(newCardRef, {
          id: cardId,
          userId: currentUserId,
          [field]: value,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        console.log(`✅ Created and saved card ${cardId}.${field}:`, value);
      } else {
        // Card exists, update it
        await updateDoc(cardDoc.ref, {
          [field]: value,
          updatedAt: Timestamp.now(),
        });
        console.log(`✅ Saved card ${cardId}.${field}:`, value);
      }
      // Don't refetch - prevents flickering
    } catch (err) {
      console.error('❌ Error saving card:', err);
      alert('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  }, [currentUserId]);

  // Upload profile photo - now with positioning
  const handlePhotoUpload = useCallback(async (file: File) => {
    if (!currentUserId || !file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadStatus({ active: true, message: '❌ Alleen afbeeldingen zijn toegestaan.', progress: 0 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus({ active: true, message: '❌ Bestand is te groot. Maximaal 5MB toegestaan.', progress: 0 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
      return;
    }

    // Create preview URL
    const previewURL = URL.createObjectURL(file);
    setTempPhotoURL(previewURL);
    setTempPhotoFile(file);
    setTempPhotoPosition({ x: 50, y: 50 }); // Center by default
    setShowImagePositioner(true);
  }, [currentUserId]);

  const handleImagePositionSave = useCallback(async () => {
    if (!tempPhotoFile || !currentUserId) return;

    setUploadingPhoto(true);
    setShowImagePositioner(false);
    setUploadStatus({ active: true, message: `Even geduld...`, progress: 10 });
    
    try {
      // Compress image before upload (max 800px for profile photos)
      setUploadStatus({ active: true, message: 'Foto uploaden...', progress: 20 });
      const compressedFile = await compressImage(tempPhotoFile, 800, 0.85);
      
      setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 40 });
      const imageRef = ref(storage, `profilePhotos/${currentUserId}/${Date.now()}-${compressedFile.name}`);
      
      setUploadStatus({ active: true, message: 'Bijna daar... 🚀', progress: 60 });
      const snapshot = await uploadBytes(imageRef, compressedFile);
      
      setUploadStatus({ active: true, message: 'Nog even de puntjes op de i...', progress: 80 });
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Optimistic update with position
      setLocalUpdates(prev => ({ ...prev, photoURL: downloadURL, photoPosition: tempPhotoPosition }));
      
      setUploadStatus({ active: true, message: 'We slaan het voor je op 💾', progress: 90 });
      await saveField('photoURL', downloadURL);
      await saveField('photoPosition', tempPhotoPosition);
      
      // Clean up
      URL.revokeObjectURL(tempPhotoURL);
      setTempPhotoFile(null);
      setTempPhotoURL('');
      
      setUploadStatus({ active: true, message: '🎉 Top! Je nieuwe foto staat er prachtig op!', progress: 100 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
    } catch (err) {
      console.error('❌ Error uploading photo:', err);
      setUploadStatus({ active: true, message: '😕 Oeps! Er ging iets mis. Probeer het opnieuw.', progress: 0 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
    } finally {
      setUploadingPhoto(false);
    }
  }, [currentUserId, tempPhotoFile, tempPhotoPosition, tempPhotoURL, saveField]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    updatePosition(e);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      updatePosition(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updatePosition = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setTempPhotoPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y))
    });
  };

  // Add expertise/interesse
  const addExpertiseItem = useCallback(async (type: 'expertise' | 'interests') => {
    if (!newExpertiseValue.trim() || !userProfile) return;
    
    const currentList = userProfile[type] || [];
    const newList = [...currentList, newExpertiseValue.trim()];
    await saveField(type, newList);
    setNewExpertiseValue('');
    setShowAddExpertise(false);
  }, [newExpertiseValue, userProfile, saveField]);

  // Remove expertise/interesse
  const removeExpertiseItem = useCallback(async (type: 'expertise' | 'interests', index: number) => {
    if (!userProfile) return;
    
    const currentList = userProfile[type] || [];
    const newList = currentList.filter((_: any, i: number) => i !== index);
    await saveField(type, newList);
  }, [userProfile, saveField]);

  // Get hero backgrounds from biografie card (must be before hero upload functions)
  const biografieCard = useMemo(() => 
    carouselCards.find((c: CarouselCard) => c.id === 'biografie'), 
    [carouselCards]
  );
  
  // Local state for hero backgrounds (for immediate display after upload)
  const [localHeroBackgrounds, setLocalHeroBackgrounds] = useState<string[]>([]);
  
  // Sync local state with Firebase data on initial load
  useEffect(() => {
    const bgs = biografieCard?.heroBackgrounds?.filter(Boolean) || [];
    if (bgs.length > 0 && localHeroBackgrounds.length === 0) {
      setLocalHeroBackgrounds(bgs);
    }
  }, [biografieCard?.heroBackgrounds, localHeroBackgrounds.length]);
  
  // Use local state if available, otherwise fall back to Firebase data
  const heroBackgrounds = useMemo(() => {
    if (localHeroBackgrounds.length > 0) {
      return localHeroBackgrounds.filter(Boolean);
    }
    return biografieCard?.heroBackgrounds?.filter(Boolean) || [];
  }, [localHeroBackgrounds, biografieCard?.heroBackgrounds]);

  // Hero background editing
  const [showHeroEditor, setShowHeroEditor] = useState(false);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);
  
  // Company media type (image or video)
  const [companyMediaType, setCompanyMediaType] = useState<'image' | 'video'>('image');
  
  // Accent color picker
  const [showColorPicker, setShowColorPicker] = useState(false);
  const accentColor = effectiveProfile?.accentColor || '#4F6660'; // Default to brand orange
  
  // Global upload status for prominent indicator
  const [uploadStatus, setUploadStatus] = useState<{ active: boolean; message: string; progress: number }>({
    active: false,
    message: '',
    progress: 0
  });

  // User's recent posts from feed
  const [userPosts, setUserPosts] = useState<Array<{
    id: string;
    content: string;
    timestamp: any;
    attachments?: Array<{ type: string; url: string }>;
    linkPreviews?: LinkPreview[];
  }>>([]);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Fetch user's posts from communityPosts collection (real-time)
  useEffect(() => {
    const targetUserId = selectedUserId || currentUserId;
    if (!targetUserId) {
      setLoadingPosts(false);
      return;
    }

    console.log('🔍 Fetching posts for userId:', targetUserId);
    
    const db = getFirestore();
    
    // Try simple query first (without orderBy to avoid index requirement)
    const postsQuery = query(
      collection(db, 'communityPosts'),
      where('userId', '==', targetUserId),
      limit(8)
    );
    
    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
      console.log('📬 Posts received:', snapshot.docs.length);
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        content: doc.data().content || '',
        timestamp: doc.data().timestamp,
        attachments: doc.data().attachments || [],
        linkPreviews: doc.data().linkPreviews || []
      }));
      
      // Sort by timestamp client-side (newest first)
      posts.sort((a, b) => {
        const timeA = a.timestamp?.toDate?.() || a.timestamp || 0;
        const timeB = b.timestamp?.toDate?.() || b.timestamp || 0;
        return new Date(timeB).getTime() - new Date(timeA).getTime();
      });
      
      setUserPosts(posts);
      setLoadingPosts(false);
    }, (error) => {
      console.error('❌ Error fetching user posts:', error);
      setLoadingPosts(false);
    });

    return () => unsubscribe();
  }, [selectedUserId, currentUserId]);

  // Upload hero background
  const handleHeroBackgroundUpload = useCallback(async (file: File, index: number) => {
    if (!currentUserId || !file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      alert('Bestand is te groot. Maximum is 10MB.');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Ongeldig bestandstype. Gebruik JPG, PNG of WebP.');
      return;
    }
    
    setUploadingHeroBg(true);
    setUploadStatus({ active: true, message: `Even geduld...`, progress: 10 });
    
    try {
      // Compress image before upload (max 1920px for hero backgrounds)
      setUploadStatus({ active: true, message: 'Je foto wordt geoptimaliseerd ✨', progress: 15 });
      const compressedFile = await compressImage(file, 1920, 0.85);
      
      setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 35 });
      const imageRef = ref(storage, `heroBackgrounds/${currentUserId}/${Date.now()}-${compressedFile.name}`);
      
      setUploadStatus({ active: true, message: 'Bijna daar... 🚀', progress: 55 });
      const snapshot = await uploadBytes(imageRef, compressedFile);
      
      setUploadStatus({ active: true, message: 'Nog even de puntjes op de i...', progress: 70 });
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Get current backgrounds and update
      const currentBgs = localHeroBackgrounds.length > 0 ? localHeroBackgrounds : (biografieCard?.heroBackgrounds || []);
      const newBgs = [...currentBgs];
      newBgs[index] = downloadURL;
      
      // Filter out empty values and ensure max 3 items
      const filteredBgs = newBgs.filter(Boolean).slice(0, 3);
      
      // Update local state immediately (optimistic update)
      setLocalHeroBackgrounds(filteredBgs);
      
      setUploadStatus({ active: true, message: 'We slaan het voor je op 💾', progress: 90 });
      // Save to Firebase in background - save filtered array
      await saveCardField('biografie', 'heroBackgrounds', filteredBgs);
      
      setUploadStatus({ active: true, message: '🎉 Mooi! Je achtergrond is toegevoegd!', progress: 100 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
      console.log('✅ Hero background uploaded:', downloadURL);
    } catch (err) {
      console.error('❌ Error uploading hero background:', err);
      setUploadStatus({ active: true, message: '😕 Oeps! Er ging iets mis. Probeer het opnieuw.', progress: 0 });
      setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
    } finally {
      setUploadingHeroBg(false);
    }
  }, [currentUserId, localHeroBackgrounds, biografieCard?.heroBackgrounds, saveCardField]);

  // Remove hero background
  const removeHeroBackground = useCallback(async (index: number) => {
    const currentBgs = localHeroBackgrounds.length > 0 ? localHeroBackgrounds : (biografieCard?.heroBackgrounds || []);
    const newBgs = currentBgs.filter((_: any, i: number) => i !== index).filter(Boolean);
    
    // Update local state immediately
    setLocalHeroBackgrounds(newBgs);
    
    // Save to Firebase
    await saveCardField('biografie', 'heroBackgrounds', newBgs);
  }, [localHeroBackgrounds, biografieCard?.heroBackgrounds, saveCardField]);

  // Ken Burns background rotation
  useEffect(() => {
    if (heroBackgrounds.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBgIndex(prev => (prev + 1) % heroBackgrounds.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [heroBackgrounds.length]);

  // Template messages for connect
  const templateMessages = [
    "Ik zou graag met je in contact komen om te netwerken.",
    "Interessant profiel! Laten we connecten.",
    "Ik denk dat we kunnen samenwerken. Laten we praten!",
    "Leuk je hier te zien! Zullen we connecten?"
  ];

  // Send connection request
  const handleSendConnectionRequest = useCallback(async () => {
    if (!currentUserId || !selectedUserId || sendingRequest) return;
    
    setSendingRequest(true);
    try {
      const { connectionRequestService } = await import('../../services/connectionRequestService');
      await connectionRequestService.sendConnectionRequest(currentUserId, selectedUserId, connectMessage || undefined);
      
      // Send inbox notification
      const { getFirestore, collection, addDoc, Timestamp } = await import('firebase/firestore');
      const db = getFirestore();
      await addDoc(collection(db, 'users', selectedUserId, 'inbox'), {
        type: 'connection_request',
        fromUserId: currentUserId,
        fromName: auth.currentUser?.displayName || 'Een gebruiker',
        message: connectMessage || 'Wil graag met je connecten',
        source: 'profiel',
        read: false,
        createdAt: Timestamp.now()
      });
      
      setRequestSent(true);
      setTimeout(() => {
        setShowConnectModal(false);
        setConnectMessage('');
        setRequestSent(false);
      }, 2000);
    } catch (err) {
      console.error('Error sending connection request:', err);
      alert('Er ging iets mis bij het versturen van het verzoek.');
    } finally {
      setSendingRequest(false);
    }
  }, [currentUserId, selectedUserId, connectMessage, sendingRequest, auth.currentUser?.displayName]);

  // Load user connections for recommend modal
  const loadUserConnections = useCallback(async () => {
    if (!currentUserId) return;
    
    setLoadingConnections(true);
    try {
      const db = getFirestore();
      
      // Get connections where user is user1 or user2
      const [snapshot1, snapshot2] = await Promise.all([
        getDocs(query(collection(db, 'connections'), where('user1Id', '==', currentUserId))),
        getDocs(query(collection(db, 'connections'), where('user2Id', '==', currentUserId)))
      ]);
      
      const connectionIds = new Set<string>();
      snapshot1.docs.forEach(doc => connectionIds.add(doc.data().user2Id));
      snapshot2.docs.forEach(doc => connectionIds.add(doc.data().user1Id));
      
      // Remove the profile being viewed from options
      if (selectedUserId) connectionIds.delete(selectedUserId);
      
      // Fetch user details for each connection
      const connections: Array<{id: string, name: string, photoURL?: string}> = [];
      for (const userId of Array.from(connectionIds)) {
        try {
          const userDoc = await getDoc(doc(db, 'userProfiles', userId));
          if (userDoc.exists()) {
            const data = userDoc.data();
            connections.push({
              id: userId,
              name: data.fullName || data.displayName || 'Onbekend',
              photoURL: data.photoURL
            });
          }
        } catch (e) {
          console.error('Error fetching connection:', e);
        }
      }
      
      setUserConnections(connections);
    } catch (err) {
      console.error('Error loading connections:', err);
    } finally {
      setLoadingConnections(false);
    }
  }, [currentUserId, selectedUserId]);

  // Load connections when recommend modal opens
  useEffect(() => {
    if (showRecommendModal) {
      loadUserConnections();
    }
  }, [showRecommendModal, loadUserConnections]);

  // Template messages for recommendations
  const recommendTemplates = [
    "Ik raad deze professional van harte aan!",
    "Een geweldige partner voor jouw business.",
    "Top expert op dit gebied, absoluut de moeite waard!",
    "Zeer betrouwbaar en professioneel. Een aanrader!"
  ];

  // Send recommendation
  const handleSendRecommendation = useCallback(async () => {
    if (!currentUserId || !selectedUserId || sendingRecommendation) return;
    if (!selectedRecommendTo && !postToNewsfeed) {
      alert('Selecteer iemand om aan te bevelen of kies om te delen in de newsfeed.');
      return;
    }
    
    setSendingRecommendation(true);
    try {
      const db = getFirestore();
      const recommendedProfile = effectiveProfile;
      const recommendedName = recommendedProfile?.fullName || recommendedProfile?.displayName || 'Iemand';
      const recommenderName = auth.currentUser?.displayName || 'Een lid';
      
      // If recommending to a specific person, send inbox notification
      if (selectedRecommendTo) {
        const selectedConnection = userConnections.find(c => c.id === selectedRecommendTo);
        
        await addDoc(collection(db, 'users', selectedRecommendTo, 'inbox'), {
          type: 'recommendation',
          fromUserId: currentUserId,
          fromName: recommenderName,
          recommendedUserId: selectedUserId,
          recommendedName: recommendedName,
          recommendedPhoto: recommendedProfile?.photoURL || null,
          message: recommendMessage || `${recommenderName} beveelt ${recommendedName} aan!`,
          read: false,
          createdAt: Timestamp.now()
        });
      }
      
      // If posting to newsfeed
      if (postToNewsfeed) {
        await addDoc(collection(db, 'communityPosts'), {
          content: `⭐ **Aanbeveling**: Ik beveel ${recommendedName} van harte aan! ${recommendMessage ? `\n\n"${recommendMessage}"` : ''}`,
          author: recommenderName,
          authorPhoto: auth.currentUser?.photoURL || null,
          userId: currentUserId,
          timestamp: Timestamp.now(),
          attachments: recommendedProfile?.photoURL ? [{ type: 'recommendation', url: recommendedProfile.photoURL, recommendedUserId: selectedUserId, recommendedName }] : [],
          type: 'recommendation',
          recommendedUserId: selectedUserId,
          recommendedName: recommendedName,
          pinned: false
        });
      }
      
      // Save recommendation to recommendations collection for LLM later
      await addDoc(collection(db, 'recommendations'), {
        fromUserId: currentUserId,
        fromName: recommenderName,
        toUserId: selectedRecommendTo || null,
        recommendedUserId: selectedUserId,
        recommendedName: recommendedName,
        message: recommendMessage,
        postedToNewsfeed: postToNewsfeed,
        createdAt: Timestamp.now()
      });
      
      setRecommendationSent(true);
      setTimeout(() => {
        setShowRecommendModal(false);
        setRecommendMessage('');
        setSelectedRecommendTo('');
        setPostToNewsfeed(true);
        setRecommendationSent(false);
      }, 2000);
    } catch (err) {
      console.error('Error sending recommendation:', err);
      alert('Er ging iets mis bij het versturen van de aanbeveling.');
    } finally {
      setSendingRecommendation(false);
    }
  }, [currentUserId, selectedUserId, sendingRecommendation, selectedRecommendTo, postToNewsfeed, recommendMessage, effectiveProfile, auth.currentUser, userConnections]);

  // Get cards by type
  const getCardsByType = useCallback((types: string[]) => {
    return carouselCards.filter((c: CarouselCard) => types.includes(c.id));
  }, [carouselCards]);

  // Expertise cards (max 6)
  const expertiseItems = useMemo(() => {
    const items: { label: string; type: 'expertise' | 'interesse' }[] = [];
    
    const expertise = biografieCard?.expertise || userProfile?.expertise || [];
    const interests = biografieCard?.interesses || userProfile?.interests || [];
    
    expertise.forEach((e: string) => items.push({ label: e, type: 'expertise' }));
    interests.forEach((i: string) => items.push({ label: i, type: 'interesse' }));
    
    // Fill with placeholders if less than 6
    while (items.length < 6) {
      items.push({ label: '', type: 'expertise' });
    }
    
    return items.slice(0, 6);
  }, [biografieCard, userProfile]);

  // Activity cards (max 6)
  const activityCards = useMemo(() => {
    const excludeIds = ['biografie', 'expertise', 'mijn-bedrijf'];
    return carouselCards
      .filter((c: CarouselCard) => !excludeIds.includes(c.id))
      .slice(0, 6);
  }, [carouselCards]);

  // Portfolio images
  const portfolioImages = useMemo(() => {
    return carouselCards
      .filter((c: CarouselCard) => c.image)
      .map((c: CarouselCard) => c.image)
      .filter((img): img is string => img !== undefined)
      .slice(0, 9);
  }, [carouselCards]);

  // Company card
  const companyCard = useMemo(() => 
    carouselCards.find((c: CarouselCard) => c.id === 'mijn-bedrijf'),
    [carouselCards]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F6660] mx-auto mb-4"></div>
          <p className="text-gray-500">Profiel laden...</p>
        </div>
      </div>
    );
  }

  if (error || !userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Profiel niet gevonden'}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#4F6660] text-white rounded-lg hover:bg-[#e55a2b]"
          >
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  // Fallback values for SEO
  const displayName = userProfile.fullName || userProfile.displayName || 'SuperKonnected Lid';
  const pageTitle = `${displayName} | SuperKonnected`;
  const pageDescription = userProfile.oneLiner || userProfile.bio || `Bekijk het profiel van ${displayName} op SuperKonnected`;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        {userProfile.photoURL && <meta property="og:image" content={userProfile.photoURL} />}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
              "@type": "Person",
              "name": displayName,
              "jobTitle": userProfile.role || undefined,
              "description": userProfile.bio || undefined,
              "image": userProfile.photoURL || undefined,
              "worksFor": userProfile.company ? {
                "@type": "Organization",
                "name": userProfile.company
              } : undefined
            }
          })}
        </script>
      </Helmet>

      {/* Terugknop — alleen zichtbaar wanneer geopend vanuit een clubpagina */}
      {onBack && (
        <div style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #F3F4F6', padding: '8px 16px' }}>
          <button
            type="button"
            onClick={onBack}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 9, border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            ← Terug
          </button>
        </div>
      )}

      {/* Global Upload Indicator - Fixed at bottom center */}
      {uploadStatus.active && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[99999]">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 px-6 py-4 min-w-[300px] animate-bounce-in">
            <div className="flex items-center gap-3 mb-2">
              {uploadStatus.progress < 100 ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#4F6660] border-t-transparent"></div>
              ) : null}
              <span className="font-medium text-gray-900">{uploadStatus.message}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#4F6660] to-[#FFB366] transition-all duration-300 ease-out rounded-full"
                style={{ width: `${uploadStatus.progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div 
        className="min-h-screen bg-gray-50"
        style={{ 
          '--accent-color': accentColor,
          '--accent-color-hover': `${accentColor}dd`,
        } as React.CSSProperties}
      >
        {/* HERO SECTION */}
        <section className="relative h-[55vh] min-h-[400px] max-h-[550px] overflow-hidden">
          {/* Background with Ken Burns effect */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentBgIndex}
              className="absolute inset-0 bg-cover bg-center"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ 
                opacity: 1, 
                scale: [1, 1.05, 1.02, 1.05, 1],
                x: [0, -15, 5, -10, 0],
                y: [0, 8, -5, 10, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 1.5 },
                scale: { duration: 28, repeat: Infinity, ease: 'linear' },
                x: { duration: 28, repeat: Infinity, ease: 'linear' },
                y: { duration: 28, repeat: Infinity, ease: 'linear' },
              }}
              style={{
                backgroundImage: heroBackgrounds.length > 0
                  ? `url(${heroBackgrounds[currentBgIndex]})`
                  : 'linear-gradient(135deg, #0A2342 0%, #1a3a5c 50%, #0A2342 100%)',
              }}
            />
          </AnimatePresence>
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
          
          {/* Animated orbs */}
          <motion.div 
            className="absolute top-20 left-20 w-64 h-64 rounded-full blur-3xl"
            style={{ backgroundColor: `${accentColor}33` }}
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div 
            className="absolute bottom-20 right-20 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"
            animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          
          {/* Hero Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            {/* Preview Mode Toggle & Saving indicator */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-20">
              {isOwnProfile && !previewMode && (
                <>
                  <button
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${
                      showColorPicker
                        ? 'bg-white text-gray-900 shadow-lg'
                        : 'bg-black/30 text-white/80 hover:bg-black/50'
                    }`}
                  >
                    <span 
                      className="w-3 h-3 rounded-full border border-white/50" 
                      style={{ backgroundColor: accentColor }}
                    />
                    Kleur
                  </button>
                  <button
                    onClick={() => setShowHeroEditor(!showHeroEditor)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                      showHeroEditor
                        ? 'bg-white text-gray-900 shadow-lg'
                        : 'bg-black/30 text-white/80 hover:bg-black/50'
                    }`}
                  >
                    🖼️ Achtergrond
                  </button>
                </>
              )}
              {isOwnProfile && (
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                    previewMode
                      ? 'text-white shadow-lg'
                      : 'bg-black/30 text-white/80 hover:bg-black/50'
                  }`}
                  style={previewMode ? { backgroundColor: accentColor } : undefined}
                >
                  {previewMode ? '✏️ Bewerken' : '👁️ Preview'}
                </button>
              )}
              {saving && (
                <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full flex items-center gap-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  Opslaan...
                </div>
              )}
            </div>

            {/* Hero Background Editor Panel */}
            {showHeroEditor && effectiveIsOwner && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-14 right-4 bg-white rounded-xl shadow-2xl p-4 z-30 w-80"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm">🖼️ Hero Achtergronden</h3>
                  <button
                    onClick={() => setShowHeroEditor(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <div className="text-xs text-gray-500 mb-3 space-y-1">
                  <p>Upload max 3 afbeeldingen. Ze wisselen automatisch met Ken Burns effect.</p>
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    <span><strong>Formaat:</strong> 1920×1080px (16:9) werkt het beste</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    <span><strong>Max grootte:</strong> 10 MB per afbeelding</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                    <span><strong>Type:</strong> JPG, PNG of WebP</span>
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[0, 1, 2].map((index) => {
                    const bg = heroBackgrounds[index] || '';
                    return (
                      <div key={index} className="relative aspect-video rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 overflow-hidden">
                        {bg ? (
                          <>
                            <img src={bg} alt={`Bg ${index + 1}`} className="w-full h-full object-cover" />
                            <button
                              onClick={() => removeHeroBackground(index)}
                              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && handleHeroBackgroundUpload(e.target.files[0], index)}
                              disabled={uploadingHeroBg}
                            />
                            {uploadingHeroBg ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            ) : (
                              <>
                                <span className="text-lg">📷</span>
                                <span className="text-[10px] text-gray-400">{index + 1}</span>
                              </>
                            )}
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Color Picker Panel */}
            {showColorPicker && effectiveIsOwner && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-14 right-4 bg-white rounded-xl shadow-2xl p-4 z-30 w-72"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 text-sm">🎨 Accent Kleur</h3>
                  <button
                    onClick={() => setShowColorPicker(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  Kies een kleur voor buttons, links en accenten op je profiel.
                </p>
                
                {/* Preset colors */}
                <div className="grid grid-cols-6 gap-2 mb-4">
                  {[
                    { color: '#4F6660', name: 'SuperKonnected Oranje' },
                    { color: '#0077B5', name: 'LinkedIn Blauw' },
                    { color: '#E4405F', name: 'Instagram Roze' },
                    { color: '#1DA1F2', name: 'Twitter Blauw' },
                    { color: '#00D4AA', name: 'Mint Groen' },
                    { color: '#9333EA', name: 'Paars' },
                    { color: '#EF4444', name: 'Rood' },
                    { color: '#F59E0B', name: 'Goud' },
                    { color: '#10B981', name: 'Groen' },
                    { color: '#3B82F6', name: 'Blauw' },
                    { color: '#6366F1', name: 'Indigo' },
                    { color: '#EC4899', name: 'Roze' },
                  ].map(({ color, name }) => (
                    <button
                      key={color}
                      onClick={() => saveField('accentColor', color)}
                      className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                        accentColor === color 
                          ? 'border-gray-900 ring-2 ring-gray-300' 
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color }}
                      title={name}
                    />
                  ))}
                </div>
                
                {/* Custom color picker */}
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <label className="text-xs text-gray-500 shrink-0">Of kies zelf:</label>
                  <div className="relative">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => saveField('accentColor', e.target.value)}
                      className="w-10 h-8 rounded cursor-pointer border border-gray-200"
                    />
                  </div>
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => {
                      const val = e.target.value;
                      // Allow typing and auto-add # if missing
                      if (val.match(/^#?[0-9A-Fa-f]{0,6}$/)) {
                        const hex = val.startsWith('#') ? val : `#${val}`;
                        if (hex.match(/^#[0-9A-Fa-f]{6}$/)) {
                          saveField('accentColor', hex.toUpperCase());
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Validate on blur, reset if invalid
                      const val = e.target.value;
                      const hex = val.startsWith('#') ? val : `#${val}`;
                      if (hex.match(/^#[0-9A-Fa-f]{6}$/)) {
                        saveField('accentColor', hex.toUpperCase());
                      }
                    }}
                    placeholder="#4F6660"
                    className="flex-1 text-xs font-mono text-gray-700 uppercase bg-gray-50 border border-gray-200 rounded px-2 py-1.5 w-20 focus:outline-none focus:ring-1 focus:ring-gray-300"
                    maxLength={7}
                  />
                </div>
                
                {/* Reset button */}
                {accentColor !== '#4F6660' && (
                  <button
                    onClick={() => saveField('accentColor', '#4F6660')}
                    className="w-full mt-3 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                  >
                    ↩️ Reset naar standaard
                  </button>
                )}
              </motion.div>
            )}
            
            {/* Avatar - Editable */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-4 relative group"
            >
              {(localUpdates.photoURL !== '' && (localUpdates.photoURL || userProfile.photoURL)) ? (
                <img 
                  src={localUpdates.photoURL || userProfile.photoURL} 
                  alt={displayName}
                  className="w-24 h-24 rounded-full border-4 border-white/30 shadow-xl object-cover"
                  style={{
                    objectPosition: userProfile.photoPosition 
                      ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` 
                      : 'center'
                  }}
                />
              ) : (
                <div 
                  className="w-24 h-24 rounded-full border-4 border-white/30 shadow-xl flex items-center justify-center text-3xl text-white font-bold"
                  style={{ background: `linear-gradient(135deg, ${accentColor}, #0A2342)` }}
                >
                  {displayName.charAt(0) || '?'}
                </div>
              )}
              {/* Photo upload overlay for owner */}
              {effectiveIsOwner && (
                <>
                  {/* Delete button for profile photo */}
                  {(localUpdates.photoURL || userProfile.photoURL) && localUpdates.photoURL !== '' && (
                    <button
                      onClick={() => {
                        setLocalUpdates(prev => ({ ...prev, photoURL: '' }));
                        saveField('photoURL', '');
                      }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full text-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center z-10 shadow-lg"
                    >
                      ×
                    </button>
                  )}
                  <label className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handlePhotoUpload(e.target.files[0])}
                      disabled={uploadingPhoto}
                    />
                    {uploadingPhoto ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    ) : (
                      <span className="text-white text-xl">📷</span>
                    )}
                  </label>
                </>
              )}
            </motion.div>
            
            {/* Name - Editable */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-2xl md:text-3xl font-semibold text-white mb-1"
            >
              <EditableText
                value={effectiveProfile?.fullName || ''}
                onSave={(value) => saveField('fullName', value)}
                isOwner={effectiveIsOwner}
                className="text-2xl md:text-3xl font-semibold text-white"
                placeholder="Naam"
              />
            </motion.h1>
            
            {/* Role - Editable */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base text-white/80 mb-6"
            >
              <EditableText
                value={effectiveProfile?.role || ''}
                onSave={(value) => saveField('role', value)}
                isOwner={effectiveIsOwner}
                className="text-base text-white/80"
                placeholder="Functie / rol"
              />
            </motion.p>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-wrap gap-3 mt-2 justify-center"
            >
              {/* Buttons for viewing other profiles OR preview mode */}
              {(!isOwnProfile || previewMode) && (
                <>
                  <button
                    onClick={() => previewMode ? alert('Dit is hoe de Connect knop eruitziet voor bezoekers!') : setShowConnectModal(true)}
                    className="px-5 py-2 text-white text-sm font-medium rounded-full transition-colors shadow-lg hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                  >
                    Connect
                  </button>
                  <button
                    onClick={() => alert(previewMode ? 'Dit is hoe de Bericht knop eruitziet voor bezoekers!' : 'Chat functie komt binnenkort!')}
                    className="px-5 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors border border-white/20"
                  >
                    💬 Bericht
                  </button>
                  <button
                    onClick={() => previewMode ? alert('Dit is hoe de Aanbevelen knop eruitziet voor bezoekers!') : setShowRecommendModal(true)}
                    className="px-5 py-2 bg-orange-600 text-white text-sm font-medium rounded-full hover:bg-orange-700 transition-colors shadow-lg"
                  >
                    ⭐ Aanbevelen
                  </button>
                </>
              )}
              
              {/* Buttons for own profile (not in preview mode) */}
              {isOwnProfile && !previewMode && (
                <>
                  <button
                    onClick={() => window.location.hash = '#profiel'}
                    className="px-5 py-2 text-white text-sm font-medium rounded-full transition-colors shadow-lg hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                  >
                    👤 Mijn Profiel
                  </button>
                  <button
                    onClick={() => window.location.hash = '#kaarten-beheer'}
                    className="px-5 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors border border-white/20"
                  >
                    ✏️ Kaarten Beheer
                  </button>
                  <button
                    onClick={() => {
                      const url = window.location.origin + '/profiel/' + currentUserId;
                      navigator.clipboard.writeText(url);
                      alert('Link gekopieerd naar klembord!');
                    }}
                    className="px-5 py-2 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full hover:bg-white/20 transition-colors border border-white/20"
                  >
                    🔗 Delen
                  </button>
                </>
              )}
            </motion.div>
          </div>
          
        </section>

        {/* BIOGRAFIE SECTION - Enhanced */}
        {(userProfile.bio || biografieCard?.description || effectiveIsOwner) && (
          <section className="py-12 px-4 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              {/* Title - Editable */}
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A2342] mb-6 text-center">
                {effectiveIsOwner ? (
                  <input
                    type="text"
                    value={localUpdates.bioTitle ?? userProfile.bioTitle ?? `Verhaal van ${userProfile.fullName?.split(' ')[0] || 'de oprichter'}`}
                    onChange={(e) => {
                      setLocalUpdates(prev => ({ ...prev, bioTitle: e.target.value }));
                    }}
                    onBlur={(e) => saveField('bioTitle', e.target.value)}
                    className="text-2xl md:text-3xl font-bold text-[#0A2342] bg-transparent text-center w-full outline-none hover:bg-gray-100 focus:bg-white focus:ring-2 focus:ring-[#4F6660]/20 rounded px-2 transition-colors"
                    placeholder="Verhaal &amp; filosofie titel..."
                  />
                ) : (
                  userProfile.bioTitle || `Verhaal van ${userProfile.fullName?.split(' ')[0] || 'de oprichter'}`
                )}
              </h2>
              
              {/* Stats Row */}
              <div className="flex justify-center gap-4 md:gap-8 mb-8">
                {[
                  {
                    value: effectiveProfile?.coreValue1 || 'Vertrouwen',
                    label: effectiveProfile?.coreValue1Subtitle || 'Transparant & afspraak = afspraak',
                    valueField: 'coreValue1',
                    labelField: 'coreValue1Subtitle',
                  },
                  {
                    value: effectiveProfile?.coreValue2 || 'Respect',
                    label: effectiveProfile?.coreValue2Subtitle || 'Luisteren en grenzen eren',
                    valueField: 'coreValue2',
                    labelField: 'coreValue2Subtitle',
                  },
                  {
                    value: effectiveProfile?.coreValue3 || 'Communicatie',
                    label: effectiveProfile?.coreValue3Subtitle || 'Binnen 24u reactie, vast kanaal',
                    valueField: 'coreValue3',
                    labelField: 'coreValue3Subtitle',
                  },
                ].map((stat, i) => (
                  <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 text-center shadow-sm min-w-[110px]">
                    {effectiveIsOwner ? (
                      <div className="flex flex-col gap-1">
                        <select
                          value={
                            stat.value === ''
                              ? '__custom'
                              : stat.value
                                ? coreValueOptions.includes(stat.value)
                                  ? stat.value
                                  : '__custom'
                                : '__placeholder'
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '__custom') {
                              // Leegmaken zodat de user vrij kan typen
                              saveField(stat.valueField, '');
                            } else if (val !== '__placeholder') {
                              saveField(stat.valueField, val);
                            }
                          }}
                          className="text-sm font-medium text-[#0A2342] bg-white/60 border border-[#0A2342]/10 rounded-md px-2 py-1 mb-1 w-full outline-none"
                        >
                          <option value="__placeholder" disabled>
                            Kies een waarde of zelf invullen
                          </option>
                          {coreValueOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                          <option value="__custom">Zelf invullen</option>
                        </select>
                        <input
                          type="text"
                          value={stat.value}
                          onChange={(e) => saveField(stat.valueField, e.target.value)}
                          className="text-xl md:text-2xl font-bold text-[#0A2342] bg-transparent text-center w-full outline-none whitespace-normal break-words"
                          placeholder="Waarde"
                        />
                        <input
                          type="text"
                          value={stat.label}
                          onChange={(e) => saveField(stat.labelField, e.target.value)}
                          className="text-[10px] md:text-xs font-semibold tracking-wider bg-transparent text-center w-full outline-none whitespace-normal break-words leading-tight"
                          style={{ color: accentColor }}
                          placeholder="Onderregel"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <div className="text-xl md:text-2xl font-bold text-[#0A2342]">{stat.value}</div>
                        <div className="text-[10px] md:text-xs font-semibold tracking-wider whitespace-normal break-words leading-tight" style={{ color: accentColor }}>
                          {stat.label}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Bio Content */}
              <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-sm">
                {effectiveIsOwner ? (
                  <textarea
                    value={effectiveProfile?.bio || ''}
                    onChange={(e) => saveField('bio', e.target.value)}
                    placeholder="Wie ben je, wat doe je, en waarom doe je dit? Noem 2-3 highlights."
                    className="w-full min-h-[200px] text-[#0A2342]/80 leading-relaxed bg-white/50 resize-none outline-none text-sm md:text-base border-2 border-gray-200 rounded-xl p-3 focus:border-[#4F6660] focus:ring-2 focus:ring-[#4F6660]/20 transition-colors"
                    style={{ whiteSpace: 'pre-wrap' }}
                  />
                ) : (
                  <div 
                    className="text-[#0A2342]/80 leading-relaxed text-sm md:text-base"
                    dangerouslySetInnerHTML={{ 
                      __html: (effectiveProfile?.bio || biografieCard?.description || '')
                        .replace(/\n/g, '<br>')
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                    }}
                  />
                )}
                
                {/* Highlighted Quote */}
                <div 
                  className="mt-6 p-4 rounded-xl border-l-4"
                  style={{ 
                    background: `linear-gradient(to right, ${accentColor}1a, ${accentColor}0d)`,
                    borderColor: accentColor 
                  }}
                >
                  {effectiveIsOwner ? (
                    <textarea
                      value={effectiveProfile?.bioQuote || ''}
                      onChange={(e) => saveField('bioQuote', e.target.value)}
                      placeholder="Je krachtigste quote of levensmotto..."
                      className="w-full text-[#0A2342] italic font-medium bg-transparent resize-none outline-none text-sm md:text-base"
                      rows={2}
                    />
                  ) : effectiveProfile?.bioQuote ? (
                    <p className="text-[#0A2342] italic font-medium text-sm md:text-base">
                      {effectiveProfile.bioQuote}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* EXPERTISE & INTERESSES */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Expertise & Interesses</h2>
            
            {/* Expertise (max 3) */}
            <div className="mb-8">
              <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wide">Expertise</h3>
              {(() => {
                const expertise: string[] = effectiveProfile?.expertise || userProfile?.expertise || [];
                // For display, filter out empty and get count
                const filledItems = expertise.filter((e: string) => e && e.trim());
                const displayCount = effectiveIsOwner ? 3 : filledItems.length;
                const gridClass = displayCount === 1 ? 'grid-cols-1' : displayCount === 2 ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
                
                if (displayCount === 0 && !effectiveIsOwner) return null;
                
                return (
                  <div className={`grid ${gridClass} gap-4`}>
                    {(effectiveIsOwner ? [0, 1, 2] : filledItems.map((_, i) => i)).map((index) => {
                      const item = effectiveIsOwner ? (expertise[index] || '') : filledItems[index];
                      
                      return (
                        <div 
                          key={`expertise-${index}`}
                          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 relative group min-h-[100px]"
                        >
                          <div className="text-lg mb-2" style={{ color: accentColor }}>💡</div>
                          {effectiveIsOwner ? (
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newExpertise = [...expertise];
                                // Ensure array has 3 slots
                                while (newExpertise.length < 3) newExpertise.push('');
                                newExpertise[index] = e.target.value;
                                saveField('expertise', newExpertise);
                              }}
                              placeholder="Typ je expertise..."
                              className="font-medium text-gray-900 mb-1 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                            />
                          ) : (
                            <h3 className="font-medium text-gray-900 mb-1">{item}</h3>
                          )}
                          <p className="text-xs" style={{ color: accentColor }}>Expertise</p>
                          {effectiveIsOwner && item && (
                            <button
                              onClick={() => {
                                const newExpertise = [...expertise];
                                while (newExpertise.length < 3) newExpertise.push('');
                                newExpertise[index] = '';
                                saveField('expertise', newExpertise);
                              }}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            
            {/* Interesses (max 3) */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wide">Interesses</h3>
              {(() => {
                const interests: string[] = effectiveProfile?.interests || userProfile?.interests || [];
                // For display, filter out empty and get count
                const filledItems = interests.filter((i: string) => i && i.trim());
                const displayCount = effectiveIsOwner ? 3 : filledItems.length;
                const gridClass = displayCount === 1 ? 'grid-cols-1' : displayCount === 2 ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3';
                
                if (displayCount === 0 && !effectiveIsOwner) return null;
                
                return (
                  <div className={`grid ${gridClass} gap-4`}>
                    {(effectiveIsOwner ? [0, 1, 2] : filledItems.map((_, i) => i)).map((index) => {
                      const item = effectiveIsOwner ? (interests[index] || '') : filledItems[index];
                      
                      return (
                        <div 
                          key={`interest-${index}`}
                          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 relative group min-h-[100px]"
                        >
                          <div className="text-lg mb-2" style={{ color: accentColor }}>⭐</div>
                          {effectiveIsOwner ? (
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => {
                                const newInterests = [...interests];
                                // Ensure array has 3 slots
                                while (newInterests.length < 3) newInterests.push('');
                                newInterests[index] = e.target.value;
                                saveField('interests', newInterests);
                              }}
                              placeholder="Typ je interesse..."
                              className="font-medium text-gray-900 mb-1 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                            />
                          ) : (
                            <h3 className="font-medium text-gray-900 mb-1">{item}</h3>
                          )}
                          <p className="text-xs" style={{ color: accentColor }}>Interesse</p>
                          {effectiveIsOwner && item && (
                            <button
                              onClick={() => {
                                const newInterests = [...interests];
                                while (newInterests.length < 3) newInterests.push('');
                                newInterests[index] = '';
                                saveField('interests', newInterests);
                              }}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        {/* MIJN BEDRIJF */}
        {(companyCard || userProfile.company || effectiveIsOwner) && (
          <section className="py-12 px-4 bg-[#0A2342]">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-white mb-8 text-center">Mijn Bedrijf</h2>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden p-0">
                {/* Hero Media (Image/Video) - Echt Edge-to-Edge Top */}
                <div className="w-full mb-8 relative group">
                  {(localUpdates.companyVideo || userProfile.companyVideo) ? (
                    <>
                      <div className="relative w-full h-64 md:h-80 overflow-hidden bg-black">
                        <video 
                          src={localUpdates.companyVideo || userProfile.companyVideo} 
                          className="w-full h-full object-cover"
                          controls
                          playsInline
                        />
                        {/* Subtiele donkere gradient overlay onderaan voor betere leesbaarheid */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                        {effectiveIsOwner && (
                          <>
                            <button
                              onClick={async () => {
                                setLocalUpdates(prev => ({ ...prev, companyVideo: '', companyImage: '' }));
                                await saveField('companyVideo', '');
                                if (userProfile.companyImage) {
                                  await saveField('companyImage', '');
                                }
                              }}
                              className="absolute top-4 right-4 w-8 h-8 bg-red-500 text-white rounded-full text-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center z-10 shadow-lg"
                            >
                              ×
                            </button>
                            <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center z-10">
                              <input
                                type="file"
                                accept="video/*"
                                className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file || !currentUserId) return;
                                  
                                  if (file.size > 50 * 1024 * 1024) {
                                    alert('Video is te groot. Maximaal 50MB toegestaan.');
                                    return;
                                  }
                                  
                                  if (!file.type.startsWith('video/')) {
                                    alert('Alleen video\'s zijn toegestaan.');
                                    return;
                                  }
                                  
                                  try {
                                    setUploadingPhoto(true);
                                    setUploadStatus({ active: true, message: 'Video wordt geüpload...', progress: 20 });
                                    
                                    const videoRef = ref(storage, `companyVideos/${currentUserId}/${Date.now()}-${file.name}`);
                                    setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 50 });
                                    const snapshot = await uploadBytes(videoRef, file);
                                    const url = await getDownloadURL(snapshot.ref);
                                    
                                    setLocalUpdates(prev => ({ ...prev, companyVideo: url, companyImage: '' }));
                                    await saveField('companyVideo', url);
                                    await saveField('companyImage', '');
                                    
                                    setUploadStatus({ active: true, message: '🎉 Video geüpload!', progress: 100 });
                                    setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
                                  } catch (err: any) {
                                    console.error('Upload error:', err);
                                    alert('Upload mislukt. Probeer opnieuw.');
                                  } finally {
                                    setUploadingPhoto(false);
                                  }
                                }}
                              />
                              <span className="text-white text-xl">{uploadingPhoto ? '⏳' : '📹'}</span>
                            </label>
                          </>
                        )}
                      </div>
                    </>
                  ) : (localUpdates.companyImage !== '' && (localUpdates.companyImage || companyCard?.image || userProfile.companyImage)) ? (
                    <>
                      <div className="relative w-full h-64 md:h-80 overflow-hidden" style={{ width: '100%' }}>
                        <img 
                          src={localUpdates.companyImage || companyCard?.image || userProfile.companyImage} 
                          alt={companyCard?.subtitle || userProfile.company}
                          className="w-full h-full object-cover"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {/* Subtiele donkere gradient overlay onderaan voor betere leesbaarheid */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                      </div>
                      {effectiveIsOwner && (
                        <>
                          <button
                            onClick={async () => {
                              setLocalUpdates(prev => ({ ...prev, companyImage: '', companyVideo: '' }));
                              await saveField('companyImage', '');
                              if (userProfile.companyVideo) {
                                await saveField('companyVideo', '');
                              }
                            }}
                            className="absolute top-4 right-4 w-8 h-8 bg-red-500 text-white rounded-full text-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center z-10 shadow-lg"
                          >
                            ×
                          </button>
                          <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                const auth = getAuth();
                                const user = auth.currentUser;
                                if (!user || !currentUserId) {
                                  alert('Je moet ingelogd zijn om bestanden te uploaden.');
                                  return;
                                }
                                
                                if (file.size > 10 * 1024 * 1024) {
                                  alert('Bestand is te groot. Maximaal 10MB toegestaan.');
                                  return;
                                }
                                
                                if (!file.type.startsWith('image/')) {
                                  alert('Alleen afbeeldingen zijn toegestaan.');
                                  return;
                                }
                                
                                try {
                                  setUploadingPhoto(true);
                                  setUploadStatus({ active: true, message: `Even geduld...`, progress: 10 });
                                  
                                  setUploadStatus({ active: true, message: 'Bedrijfsfoto uploaden...', progress: 20 });
                                  const compressedFile = await compressImage(file, 1200, 0.85);
                                  
                                  const imageRef = ref(storage, `companyImages/${currentUserId}/${Date.now()}-${compressedFile.name}`);
                                  
                                  setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 50 });
                                  const snapshot = await uploadBytes(imageRef, compressedFile);
                                  
                                  setUploadStatus({ active: true, message: 'Bijna daar... 🚀', progress: 70 });
                                  const url = await getDownloadURL(snapshot.ref);
                                  
                                  setLocalUpdates(prev => ({ ...prev, companyImage: url }));
                                  
                                  setUploadStatus({ active: true, message: 'We slaan het voor je op 💾', progress: 90 });
                                  await saveField('companyImage', url);
                                  
                                  setUploadStatus({ active: true, message: '🎉 Mooi! Je bedrijfsfoto is bijgewerkt!', progress: 100 });
                                  setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
                                } catch (err: any) {
                                  console.error('❌ Upload error details:', err);
                                  setUploadStatus({ active: true, message: '😕 Oeps! Er ging iets mis. Probeer het opnieuw.', progress: 0 });
                                  setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
                                } finally {
                                  setUploadingPhoto(false);
                                }
                              }}
                            />
                            <span className="text-white text-xl">{uploadingPhoto ? '⏳' : '📷'}</span>
                          </label>
                        </>
                      )}
                    </>
                  ) : effectiveIsOwner ? (
                    <div className="w-full h-64 md:h-80 border-2 border-dashed border-white/30 rounded-lg flex flex-col items-center justify-center bg-white/5 relative">
                      {/* Toggle tussen Image en Video - Bovenop geplaatst */}
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 flex gap-4 md:gap-6 z-20">
                        <button
                          type="button"
                          onClick={() => setCompanyMediaType('image')}
                          className={`px-5 py-2.5 rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-sm ${
                            companyMediaType === 'image'
                              ? 'bg-white/30 text-white shadow-lg border border-white/30'
                              : 'bg-white/15 text-white/80 hover:bg-white/25 border border-white/20'
                          }`}
                        >
                          <span className="text-base">📷</span>
                          <span>Foto</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCompanyMediaType('video')}
                          className={`px-5 py-2.5 rounded-lg transition-all text-sm font-medium flex items-center justify-center gap-2 backdrop-blur-sm ${
                            companyMediaType === 'video'
                              ? 'bg-white/30 text-white shadow-lg border border-white/30'
                              : 'bg-white/15 text-white/80 hover:bg-white/25 border border-white/20'
                          }`}
                        >
                          <span className="text-base">🎥</span>
                          <span>Video</span>
                        </button>
                      </div>
                      
                      {/* Image Upload */}
                      {companyMediaType === 'image' ? (
                        <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              const auth = getAuth();
                              const user = auth.currentUser;
                              if (!user || !currentUserId) {
                                alert('Je moet ingelogd zijn om bestanden te uploaden.');
                                return;
                              }
                              
                              if (file.size > 10 * 1024 * 1024) {
                                alert('Bestand is te groot. Maximaal 10MB toegestaan.');
                                return;
                              }
                              
                              if (!file.type.startsWith('image/')) {
                                alert('Alleen afbeeldingen zijn toegestaan.');
                                return;
                              }
                              
                              try {
                                setUploadingPhoto(true);
                                setUploadStatus({ active: true, message: `Even geduld...`, progress: 10 });
                                
                                setUploadStatus({ active: true, message: 'Je bedrijfsfoto wordt geoptimaliseerd ✨', progress: 20 });
                                const compressedFile = await compressImage(file, 1200, 0.85);
                                
                                const imageRef = ref(storage, `companyImages/${currentUserId}/${Date.now()}-${compressedFile.name}`);
                                
                                setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 50 });
                                const snapshot = await uploadBytes(imageRef, compressedFile);
                                
                                setUploadStatus({ active: true, message: 'Bijna daar... 🚀', progress: 70 });
                                const url = await getDownloadURL(snapshot.ref);
                                
                                setLocalUpdates(prev => ({ ...prev, companyImage: url, companyVideo: '' }));
                                
                                setUploadStatus({ active: true, message: 'We slaan het voor je op 💾', progress: 90 });
                                await saveField('companyImage', url);
                                if (userProfile.companyVideo) {
                                  await saveField('companyVideo', '');
                                }
                                
                                setUploadStatus({ active: true, message: '🎉 Perfect! Je bedrijfsfoto ziet er geweldig uit!', progress: 100 });
                                setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
                              } catch (err: any) {
                                console.error('❌ Upload error details:', err);
                                setUploadStatus({ active: true, message: '😕 Oeps! Er ging iets mis. Probeer het opnieuw.', progress: 0 });
                                setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 3000);
                              } finally {
                                setUploadingPhoto(false);
                              }
                            }}
                          />
                          {uploadingPhoto ? (
                            <>
                              <span className="text-2xl mb-2">⏳</span>
                              <span className="text-white/60 text-sm">Even geduld...</span>
                            </>
                          ) : (
                            <>
                              <span className="text-3xl mb-2">🏢</span>
                              <span className="text-white/60 text-sm">Bedrijfsfoto</span>
                            </>
                          )}
                        </label>
                      ) : (
                        <label className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer">
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              const auth = getAuth();
                              const user = auth.currentUser;
                              if (!user || !currentUserId) {
                                alert('Je moet ingelogd zijn om bestanden te uploaden.');
                                return;
                              }
                              
                              if (file.size > 100 * 1024 * 1024) {
                                alert('Video is te groot. Maximaal 100MB toegestaan (wordt automatisch gecomprimeerd).');
                                return;
                              }
                              
                              if (!file.type.startsWith('video/')) {
                                alert('Alleen video\'s zijn toegestaan.');
                                return;
                              }
                              
                              try {
                                setUploadingPhoto(true);
                                
                                // Compress video if needed
                                let fileToUpload = file;
                                const fileSizeMB = file.size / 1024 / 1024;
                                
                                if (fileSizeMB > 10) {
                                  setUploadStatus({ active: true, message: '📹 Video comprimeren...', progress: 10 });
                                  console.log(`Original size: ${fileSizeMB.toFixed(2)} MB`);
                                  
                                  try {
                                    // Dynamic import to reduce bundle size
                                    const { videoCompressionService } = await import('../../services/videoCompressionService');
                                    const result = await videoCompressionService.compressVideo(file, {
                                      maxWidth: 1280,
                                      maxHeight: 720,
                                      quality: 0.7,
                                      maxSizeMB: 25
                                    });
                                    
                                    fileToUpload = result.file;
                                    const savedMB = (result.originalSize - result.compressedSize) / 1024 / 1024;
                                    console.log(`✅ Compressed: ${savedMB.toFixed(2)} MB saved (${result.compressionRatio.toFixed(1)}%)`);
                                    setUploadStatus({ active: true, message: `✅ ${savedMB.toFixed(1)} MB bespaard!`, progress: 40 });
                                  } catch (compressionError) {
                                    console.warn('Compression failed, uploading original:', compressionError);
                                    setUploadStatus({ active: true, message: 'Compressie overgeslagen...', progress: 30 });
                                  }
                                }
                                
                                setUploadStatus({ active: true, message: 'Uploaden... 🚀', progress: 60 });
                                const videoRef = ref(storage, `companyVideos/${currentUserId}/${Date.now()}-${fileToUpload.name}`);
                                const snapshot = await uploadBytes(videoRef, fileToUpload);
                                const url = await getDownloadURL(snapshot.ref);
                                
                                setLocalUpdates(prev => ({ ...prev, companyVideo: url, companyImage: '' }));
                                await saveField('companyVideo', url);
                                await saveField('companyImage', '');
                                
                                setUploadStatus({ active: true, message: '🎉 Video geüpload!', progress: 100 });
                                setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 1500);
                              } catch (err: any) {
                                console.error('Upload error:', err);
                                alert('Upload mislukt. Probeer opnieuw.');
                              } finally {
                                setUploadingPhoto(false);
                              }
                            }}
                          />
                          {uploadingPhoto ? (
                            <>
                              <span className="text-2xl mb-2">⏳</span>
                              <span className="text-white/60 text-sm">Video uploaden...</span>
                            </>
                          ) : (
                            <>
                              <span className="text-3xl mb-2">🎥</span>
                              <span className="text-white/60 text-sm">Introductie video</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                  ) : null}
                </div>
                
                {/* Content met padding */}
                <div className="px-6 md:px-8 pb-6 md:pb-8">
                  {/* Company Name - Logo WEGGEHAALD, alleen naam prominent */}
                  <div className="mb-6 text-center">
                    <h3 className="text-2xl md:text-3xl font-bold text-white break-words px-4">
                      <EditableText
                        value={effectiveProfile?.company || companyCard?.subtitle || ''}
                        onSave={(value) => saveField('company', value)}
                        isOwner={effectiveIsOwner}
                        className="text-2xl md:text-3xl font-bold text-white text-center break-words"
                        placeholder="Bedrijfsnaam"
                      />
                    </h3>
                  </div>
                  
                  {/* Company Description - Full Width Bottom */}
                  <div className="w-full mt-6">
                  {effectiveIsOwner ? (
                    <div className="relative">
                      <textarea
                        value={effectiveProfile?.companyDescription || companyCard?.description || ''}
                        onChange={(e) => saveField('companyDescription', e.target.value)}
                        placeholder="Beschrijf je bedrijf en wat jullie doen. Je kunt hier meerdere alinea's gebruiken..."
                        className="w-full min-h-[150px] text-white/90 leading-relaxed bg-white/5 border border-white/20 rounded-xl p-4 outline-none focus:border-[#4F6660]/50 focus:ring-2 focus:ring-[#4F6660]/20 transition-colors resize-y text-sm md:text-base"
                        style={{ whiteSpace: 'pre-wrap' }}
                      />
                    </div>
                  ) : (
                    <div 
                      className="text-white/90 leading-relaxed text-sm md:text-base whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ 
                        __html: (effectiveProfile?.companyDescription || companyCard?.description || '')
                          .replace(/\n/g, '<br>')
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      }}
                    />
                  )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ACTIVITEIT & ERVARING */}
        {(activityCards.length > 0 || effectiveIsOwner) && (
          <section className="py-12 px-4 bg-white">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Ervaring</h2>
              <div className="space-y-4">
                {(() => {
                  // Use activities from profile or fall back to activityCards
                  const activities: Array<{title: string; subtitle: string; description: string}> = 
                    effectiveProfile?.activities || 
                    activityCards.map((c: CarouselCard) => ({ title: c.title, subtitle: c.subtitle || '', description: c.description || '' }));
                  
                  const displayActivities = effectiveIsOwner 
                    ? [...activities, ...(activities.length < 6 ? [{ title: '', subtitle: '', description: '' }] : [])]
                    : activities.filter(a => a.title);
                  
                  return displayActivities.map((activity, index) => (
                    <div 
                      key={`activity-${index}`}
                      className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100 relative group"
                    >
                      <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white text-xl shrink-0"
                  style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}99)` }}
                >
                        {activity.title ? index + 1 : '+'}
                      </div>
                      <div className="flex-1">
                        {effectiveIsOwner ? (
                          <>
                            <input
                              type="text"
                              value={activity.title}
                              onChange={(e) => {
                                const newActivities = [...(effectiveProfile?.activities || activities)];
                                // Ensure we have enough slots
                                while (newActivities.length <= index) {
                                  newActivities.push({ title: '', subtitle: '', description: '' });
                                }
                                newActivities[index] = { ...newActivities[index], title: e.target.value };
                                saveField('activities', newActivities);
                              }}
                              placeholder="Functie · Bedrijfsnaam"
                              className="font-medium text-gray-900 mb-1 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                            />
                            <input
                              type="text"
                              value={activity.subtitle}
                              onChange={(e) => {
                                const newActivities = [...(effectiveProfile?.activities || activities)];
                                while (newActivities.length <= index) {
                                  newActivities.push({ title: '', subtitle: '', description: '' });
                                }
                                newActivities[index] = { ...newActivities[index], subtitle: e.target.value };
                                saveField('activities', newActivities);
                              }}
                              placeholder="Periode (bijv. jan 2020 - heden)"
                              className="text-sm mb-2 bg-transparent border-b border-dashed border-gray-200 hover:border-gray-300 outline-none w-full"
                              style={{ color: accentColor }}
                            />
                            <textarea
                              value={activity.description}
                              onChange={(e) => {
                                const newActivities = [...(effectiveProfile?.activities || activities)];
                                while (newActivities.length <= index) {
                                  newActivities.push({ title: '', subtitle: '', description: '' });
                                }
                                newActivities[index] = { ...newActivities[index], description: e.target.value };
                                saveField('activities', newActivities);
                              }}
                              placeholder="Beschrijving van je rol en verantwoordelijkheden..."
                              className="text-sm text-gray-600 bg-transparent border border-dashed border-gray-200 hover:border-gray-300 focus:border-[#4F6660] outline-none w-full rounded-lg p-2 resize-y min-h-[60px]"
                            />
                          </>
                        ) : (
                          <>
                            <h3 className="font-medium text-gray-900 mb-1">{activity.title}</h3>
                            {activity.subtitle && <p className="text-sm mb-2" style={{ color: accentColor }}>{activity.subtitle}</p>}
                            <p className="text-sm text-gray-600">{activity.description}</p>
                          </>
                        )}
                      </div>
                      {/* Remove button */}
                      {effectiveIsOwner && activity.title && (
                        <button
                          onClick={() => {
                            const newActivities = (effectiveProfile?.activities || activities)
                              .filter((_: any, i: number) => i !== index);
                            saveField('activities', newActivities);
                          }}
                          className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ));
                })()}
              </div>
              {/* Add more button */}
              {effectiveIsOwner && (effectiveProfile?.activities?.length || 0) < 6 && (effectiveProfile?.activities?.every((a: any) => a.title) ?? true) && (
                <button
                  onClick={() => {
                    const currentActivities = effectiveProfile?.activities || [];
                    if (currentActivities.length < 6) {
                      saveField('activities', [...currentActivities, { title: '', subtitle: '', description: '' }]);
                    }
                  }}
                  className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-[#4F6660] hover:text-[#4F6660] transition-colors"
                >
                  + Ervaring toevoegen
                </button>
              )}
            </div>
          </section>
        )}

        {/* MISSIE & VISIE */}
        {(userProfile.missie || userProfile.visie || effectiveIsOwner) && (
          <section className="py-12 px-4 bg-white">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Missie & Visie</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Missie */}
                {(userProfile.missie || effectiveIsOwner) && (
                  <div className="bg-[#0A2342] rounded-2xl p-6 md:p-8 text-white">
                    <h3 className="text-lg font-semibold mb-4">Missie</h3>
                    {effectiveIsOwner ? (
                      <textarea
                        value={effectiveProfile?.missie || ''}
                        onChange={(e) => saveField('missie', e.target.value)}
                        placeholder="Wat is je missie? Waar sta je voor?"
                        className="w-full min-h-[100px] text-white/90 leading-relaxed bg-white/10 border border-white/20 rounded-xl p-3 outline-none focus:border-white/40 transition-colors resize-y text-sm md:text-base"
                      />
                    ) : (
                      <div 
                        className="text-white/80 leading-relaxed text-sm md:text-base"
                        dangerouslySetInnerHTML={{ 
                          __html: (effectiveProfile?.missie || '')
                            .replace(/\n/g, '<br>')
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        }}
                      />
                    )}
                  </div>
                )}
                {/* Visie */}
                {(userProfile.visie || effectiveIsOwner) && (
                  <div 
                    className="rounded-2xl p-6 md:p-8 text-white"
                    style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor}99)` }}
                  >
                    <h3 className="text-lg font-semibold mb-4">Visie</h3>
                    {effectiveIsOwner ? (
                      <textarea
                        value={effectiveProfile?.visie || ''}
                        onChange={(e) => saveField('visie', e.target.value)}
                        placeholder="Wat is je visie? Waar wil je naartoe?"
                        className="w-full min-h-[100px] text-white/90 leading-relaxed bg-white/10 border border-white/20 rounded-xl p-3 outline-none focus:border-white/40 transition-colors resize-y text-sm md:text-base"
                      />
                    ) : (
                      <div 
                        className="text-white/80 leading-relaxed text-sm md:text-base"
                        dangerouslySetInnerHTML={{ 
                          __html: (effectiveProfile?.visie || '')
                            .replace(/\n/g, '<br>')
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* BEDRIJFS PORTFOLIO */}
        {portfolioImages.length > 0 && (
          <section className="py-12 px-4 bg-gray-50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Bedrijfs Portfolio</h2>
              <div className="grid grid-cols-3 gap-3">
                {portfolioImages.map((img: string, index: number) => (
                  <div key={index} className="aspect-square rounded-xl overflow-hidden">
                    <img 
                      src={img} 
                      alt={`Portfolio ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* RECENT GEDEELD - Linked to News Feed */}
        <section className="py-12 px-4 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Recent Gedeeld</h2>
            {loadingPosts ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4F6660]"></div>
              </div>
            ) : userPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userPosts.map((post) => {
                  const hasMedia = post.attachments && post.attachments.length > 0;
                  const firstImage = post.attachments?.find((a: any) => a.type === 'image' || a.type === 'photo');
                  const timestamp = post.timestamp?.toDate?.() || post.timestamp;
                  const timeAgo = timestamp ? formatTimeAgo(new Date(timestamp)) : '';
                  
                  const hasLinkPreview = post.linkPreviews && post.linkPreviews.length > 0;
                  
                  return (
                    <div 
                      key={post.id} 
                      className="bg-gray-50 rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow"
                    >
                      {/* Post content */}
                      <p className="text-gray-800 text-sm line-clamp-3 mb-3">
                        {post.content || 'Geen tekst'}
                      </p>
                      
                      {/* Media preview */}
                      {firstImage && (
                        <div className="mb-3 rounded-lg overflow-hidden">
                          <img 
                            src={firstImage.url} 
                            alt="Post media" 
                            className="w-full h-32 object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Link preview */}
                      {hasLinkPreview && !firstImage && (
                        <div className="mb-3">
                          <LinkPreviewCard 
                            preview={post.linkPreviews![0]} 
                            compact={true}
                          />
                        </div>
                      )}
                      
                      {/* Timestamp */}
                      <p className="text-xs text-gray-400">{timeAgo}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-16 bg-orange-50 rounded-xl border-2 border-dashed border-orange-200">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {effectiveIsOwner 
                    ? 'Deel je eerste bericht!' 
                    : 'Nog geen berichten gedeeld'}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {effectiveIsOwner 
                    ? 'Begin met het delen van waardevolle content met de community. Je berichten verschijnen hier automatisch!' 
                    : 'Deze gebruiker heeft nog geen berichten gedeeld in de community.'}
                </p>
                {effectiveIsOwner && (
                  <button
                    onClick={() => {
                      window.location.hash = '#dashboard';
                    }}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white font-medium rounded-full hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    <span>✍️</span>
                    <span>Plaats je eerste post</span>
                    <span>→</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* DIENSTEN & PRODUCTEN */}
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-8 text-center">Diensten & Producten</h2>
            {(() => {
              const services: Array<{
                id?: string;
                title: string;
                description: string;
                price?: string;
                imageUrl?: string;
                link?: string;
              }> = effectiveProfile?.services || userProfile?.services || [];
              
              const filledServices = services.filter(s => s.title && s.title.trim() !== '');
              
              return (
                <div>
                  {effectiveIsOwner ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {[0, 1, 2, 3, 4, 5].map((index) => {
                        const service = services[index] || { title: '', description: '', price: '', imageUrl: '', link: '' };
                        
                        return (
                          <div 
                            key={`service-${index}`}
                            className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 relative group min-h-[280px] flex flex-col"
                          >
                            {/* Image upload/display */}
                            <div className="mb-3">
                              {service.imageUrl ? (
                                <div className="relative">
                                  <img 
                                    src={service.imageUrl} 
                                    alt={service.title || 'Service'} 
                                    className="w-full h-32 object-cover rounded-lg"
                                  />
                                  {effectiveIsOwner && (
                                    <button
                                      onClick={async () => {
                                        const currentServices = [...(effectiveProfile?.services || [])];
                                        while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                        currentServices[index] = { ...currentServices[index], imageUrl: '' };
                                        saveField('services', currentServices);
                                      }}
                                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ) : effectiveIsOwner ? (
                                <label className="block w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors flex items-center justify-center">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file || !currentUserId) return;
                                      
                                      // Compress image
                                      const compressedFile = await compressImage(file, 800, 0.85);
                                      
                                      try {
                                        setUploadingPhoto(true);
                                        setUploadStatus({ active: true, message: 'Afbeelding wordt geüpload...', progress: 30 });
                                        const imageRef = ref(storage, `serviceImages/${currentUserId}/${Date.now()}-${compressedFile.name}`);
                                        const snapshot = await uploadBytes(imageRef, compressedFile);
                                        const url = await getDownloadURL(snapshot.ref);
                                        
                                        const currentServices = [...(effectiveProfile?.services || [])];
                                        while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                        currentServices[index] = { ...currentServices[index], imageUrl: url };
                                        saveField('services', currentServices);
                                        setUploadStatus({ active: true, message: 'Afbeelding geüpload! ✅', progress: 100 });
                                        setTimeout(() => setUploadStatus({ active: false, message: '', progress: 0 }), 2000);
                                      } catch (error) {
                                        console.error('Upload failed:', error);
                                        alert('Upload mislukt. Probeer opnieuw.');
                                      } finally {
                                        setUploadingPhoto(false);
                                      }
                                    }}
                                  />
                                  <span className="text-gray-400 text-xs">📷 Foto</span>
                                </label>
                              ) : null}
                            </div>
                            
                            {/* Title */}
                            {effectiveIsOwner ? (
                              <input
                                type="text"
                                value={service.title || ''}
                                onChange={(e) => {
                                  const currentServices = [...(effectiveProfile?.services || [])];
                                  while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                  currentServices[index] = { ...currentServices[index], title: e.target.value };
                                  saveField('services', currentServices);
                                }}
                                placeholder="Naam van dienst/product"
                                className="font-medium text-gray-900 mb-2 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                              />
                            ) : service.title ? (
                              <h3 className="font-medium text-gray-900 mb-2">{service.title}</h3>
                            ) : null}
                            
                            {/* Description */}
                            {effectiveIsOwner ? (
                              <textarea
                                value={service.description || ''}
                                onChange={(e) => {
                                  const currentServices = [...(effectiveProfile?.services || [])];
                                  while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                  currentServices[index] = { ...currentServices[index], description: e.target.value };
                                  saveField('services', currentServices);
                                }}
                                placeholder="Beschrijving..."
                                className="text-sm text-gray-600 mb-2 bg-transparent border border-dashed border-gray-200 hover:border-gray-300 focus:border-[#4F6660] outline-none w-full rounded-lg p-2 resize-y min-h-[60px]"
                              />
                            ) : service.description ? (
                              <p className="text-sm text-gray-600 mb-2 line-clamp-3">{service.description}</p>
                            ) : null}
                            
                            {/* Price */}
                            {effectiveIsOwner ? (
                              <input
                                type="text"
                                value={service.price || ''}
                                onChange={(e) => {
                                  const currentServices = [...(effectiveProfile?.services || [])];
                                  while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                  currentServices[index] = { ...currentServices[index], price: e.target.value };
                                  saveField('services', currentServices);
                                }}
                                placeholder="Prijs (optioneel)"
                                className="text-sm font-semibold mb-2 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                                style={{ color: accentColor }}
                              />
                            ) : service.price ? (
                              <p className="text-sm font-semibold mb-2" style={{ color: accentColor }}>{service.price}</p>
                            ) : null}
                            
                            {/* Link */}
                            {effectiveIsOwner ? (
                              <input
                                type="url"
                                value={service.link || ''}
                                onChange={(e) => {
                                  const currentServices = [...(effectiveProfile?.services || [])];
                                  while (currentServices.length <= index) currentServices.push({ title: '', description: '', price: '', imageUrl: '', link: '' });
                                  currentServices[index] = { ...currentServices[index], link: e.target.value };
                                  saveField('services', currentServices);
                                }}
                                placeholder="Link (optioneel)"
                                className="text-xs text-gray-500 bg-transparent border-b border-dashed border-gray-300 hover:border-gray-400 focus:border-[#4F6660] outline-none w-full"
                              />
                            ) : service.link ? (
                              <a 
                                href={service.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-gray-500 hover:underline"
                                style={{ color: accentColor }}
                              >
                                Meer informatie →
                              </a>
                            ) : null}
                            
                            {/* Delete button */}
                            {effectiveIsOwner && (service.title || service.description || service.imageUrl) && (
                              <button
                                onClick={() => {
                                  const currentServices = [...(effectiveProfile?.services || [])];
                                  currentServices[index] = { title: '', description: '', price: '', imageUrl: '', link: '' };
                                  saveField('services', currentServices);
                                }}
                                className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 flex items-center justify-center"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : filledServices.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filledServices.map((service, index) => (
                        <div 
                          key={`service-${index}`}
                          className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                        >
                          {service.imageUrl && (
                            <img 
                              src={service.imageUrl} 
                              alt={service.title} 
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                          )}
                          <h3 className="font-medium text-gray-900 mb-2">{service.title}</h3>
                          {service.description && (
                            <p className="text-sm text-gray-600 mb-2 line-clamp-3">{service.description}</p>
                          )}
                          {service.price && (
                            <p className="text-sm font-semibold mb-2" style={{ color: accentColor }}>{service.price}</p>
                          )}
                          {service.link && (
                            <a 
                              href={service.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs hover:underline inline-block"
                              style={{ color: accentColor }}
                            >
                              Meer informatie →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl">
                      <div className="text-4xl mb-3">🛍️</div>
                      <p className="text-gray-500">
                        {effectiveIsOwner 
                          ? 'Voeg je eerste dienst of product toe!' 
                          : 'Nog geen diensten of producten beschikbaar.'}
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
            
            {/* Connect CTA Button - Only for visitors */}
            {!effectiveIsOwner && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setShowConnectModal(true)}
                  className="px-8 py-3 text-white text-base font-semibold rounded-full transition-all shadow-lg hover:opacity-90 hover:shadow-xl"
                  style={{ backgroundColor: accentColor }}
                >
                  Connect met {effectiveProfile?.fullName || effectiveProfile?.displayName || 'deze persoon'}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* FOOTER / CONTACT */}
        <footer className="bg-gray-900 text-white py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {/* Footer Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              
              {/* Column 1: Profile Summary */}
              <div className="md:col-span-1">
                <div className="flex items-center gap-3 mb-4">
                  {effectiveProfile?.photoURL && (
                    <img 
                      src={effectiveProfile.photoURL} 
                      alt="" 
                      className="w-12 h-12 rounded-full object-cover border-2 border-white/20"
                      style={{
                        objectPosition: effectiveProfile.photoPosition 
                          ? `${effectiveProfile.photoPosition.x}% ${effectiveProfile.photoPosition.y}%` 
                          : 'center'
                      }}
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-white">
                      {effectiveProfile?.displayName || effectiveProfile?.fullName || 'Naam'}
                    </h3>
                    <p className="text-xs text-gray-400">{effectiveProfile?.company || ''}</p>
                  </div>
                </div>
                {/* Show first experience/role instead of bio */}
                {(() => {
                  const firstExperience = effectiveProfile?.activities?.[0] || activityCards[0];
                  if (firstExperience?.title) {
                    return (
                      <div className="text-sm text-gray-400">
                        <p className="font-medium text-gray-300">{firstExperience.title}</p>
                        {firstExperience.subtitle && (
                          <p className="text-xs text-gray-500 mt-1">{firstExperience.subtitle}</p>
                        )}
                      </div>
                    );
                  }
                  return <p className="text-sm text-gray-400">Ondernemer & Professional</p>;
                })()}
              </div>
              
              {/* Column 2: Contact Info */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-4">Contact</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500">📧</span>
                    <EditableText
                      value={effectiveProfile?.email || ''}
                      onSave={(value) => saveField('email', value)}
                      isOwner={effectiveIsOwner}
                      className="text-gray-300 text-sm hover:text-white transition-colors"
                      placeholder="je@email.com"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500">📱</span>
                    <EditableText
                      value={effectiveProfile?.phone || ''}
                      onSave={(value) => saveField('phone', value)}
                      isOwner={effectiveIsOwner}
                      className="text-gray-300 text-sm hover:text-white transition-colors"
                      placeholder="+31 6 12345678"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500">📍</span>
                    <EditableText
                      value={effectiveProfile?.companyLocation || ''}
                      onSave={(value) => saveField('companyLocation', value)}
                      isOwner={effectiveIsOwner}
                      className="text-gray-300 text-sm hover:text-white transition-colors"
                      placeholder="Amsterdam, Nederland"
                    />
                  </div>
                </div>
              </div>
              
              {/* Column 3: Zakelijk */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-4">Zakelijk</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500">🏢</span>
                    <EditableText
                      value={effectiveProfile?.company || ''}
                      onSave={(value) => saveField('company', value)}
                      isOwner={effectiveIsOwner}
                      className="text-gray-300 text-sm hover:text-white transition-colors"
                      placeholder="Bedrijfsnaam"
                    />
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500">📋</span>
                    <div className="text-gray-300 text-sm">
                      <span className="text-gray-500">KVK: </span>
                      <EditableText
                        value={effectiveProfile?.kvkNumber || ''}
                        onSave={(value) => saveField('kvkNumber', value)}
                        isOwner={effectiveIsOwner}
                        className="text-gray-300 hover:text-white transition-colors inline"
                        placeholder="12345678"
                      />
                    </div>
                  </div>
                  {effectiveProfile?.website && (
                    <div className="flex items-start gap-2">
                      <span className="text-gray-500">🌐</span>
                      <a 
                        href={effectiveProfile.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-300 text-sm hover:text-[#4F6660] transition-colors"
                      >
                        {effectiveProfile.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Column 4: Social Links */}
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-4">Volg mij</h4>
                {effectiveIsOwner ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 mb-2">👁 = zichtbaar voor bezoekers</p>
                    {[
                      { key: 'website', label: '🌐', placeholder: 'https://jouw-website.nl' },
                      { key: 'linkedin', label: '💼', placeholder: 'linkedin.com/in/...' },
                      { key: 'instagram', label: '📸', placeholder: 'instagram.com/...' },
                      { key: 'youtube', label: '🎬', placeholder: 'youtube.com/@...' },
                      { key: 'twitter', label: '𝕏', placeholder: 'twitter.com/...' },
                      { key: 'facebook', label: '📘', placeholder: 'facebook.com/...' },
                      { key: 'tiktok', label: '🎵', placeholder: 'tiktok.com/@...' },
                    ].map(({ key, label, placeholder }) => (
                      <SocialLinkInput
                        key={key}
                        fieldKey={key}
                        label={label}
                        placeholder={placeholder}
                        value={(effectiveProfile as any)?.[key] || ''}
                        isVisible={(effectiveProfile?.socialVisibility as any)?.[key] !== false}
                        onSave={(value) => saveField(key, value)}
                        onToggleVisibility={(visible) => {
                          const currentVisibility = effectiveProfile?.socialVisibility || {};
                          saveField('socialVisibility', { ...currentVisibility, [key]: visible });
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {userProfile.linkedin && userProfile.socialVisibility?.linkedin !== false && (
                      <a href={userProfile.linkedin} target="_blank" rel="noopener noreferrer" 
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#0077B5] transition-colors">
                        💼
                      </a>
                    )}
                    {userProfile.instagram && userProfile.socialVisibility?.instagram !== false && (
                      <a href={userProfile.instagram} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors">
                        📸
                      </a>
                    )}
                    {userProfile.youtube && userProfile.socialVisibility?.youtube !== false && (
                      <a href={userProfile.youtube} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FF0000] transition-colors">
                        🎬
                      </a>
                    )}
                    {userProfile.twitter && userProfile.socialVisibility?.twitter !== false && (
                      <a href={userProfile.twitter} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-black transition-colors">
                        𝕏
                      </a>
                    )}
                    {userProfile.facebook && userProfile.socialVisibility?.facebook !== false && (
                      <a href={userProfile.facebook} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#1877F2] transition-colors">
                        📘
                      </a>
                    )}
                    {userProfile.tiktok && userProfile.socialVisibility?.tiktok !== false && (
                      <a href={userProfile.tiktok} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-black transition-colors">
                        🎵
                      </a>
                    )}
                    {userProfile.website && userProfile.socialVisibility?.website !== false && (
                      <a href={userProfile.website} target="_blank" rel="noopener noreferrer"
                        className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors">
                        🌐
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Google Maps - Show location if available */}
            {effectiveProfile?.companyLocation && (
              <div className="mb-8">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-400 mb-4">📍 Locatie</h4>
                <div className="rounded-xl overflow-hidden border border-white/10 bg-gray-800">
                  <iframe
                    title="Bedrijfslocatie"
                    width="100%"
                    height="250"
                    style={{ border: 0, filter: 'grayscale(20%) contrast(1.1)' }}
                    loading="lazy"
                    allowFullScreen
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(effectiveProfile.companyLocation)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-sm text-gray-400">
                    {effectiveProfile.companyLocation}
                  </p>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(effectiveProfile.companyLocation)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm transition-colors hover:opacity-75"
                    style={{ color: accentColor }}
                  >
                    <span>Routebeschrijving</span>
                    <span>→</span>
                  </a>
                </div>
              </div>
            )}
            
            {/* Footer Bottom */}
            <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} {effectiveProfile?.company || effectiveProfile?.displayName || 'Profiel'}. Alle rechten voorbehouden.
              </p>
              <p className="text-xs text-gray-500">
                Lid van <span style={{ color: accentColor }}>SuperKonnected</span> Community
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* CONNECT MODAL */}
      {showConnectModal && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConnectModal(false);
              setConnectMessage('');
              setRequestSent(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {requestSent ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Verzoek Verzonden!</h3>
                <p className="text-gray-600">Je connectieverzoek is verstuurd naar {displayName}</p>
              </div>
            ) : (
              <>
                {/* Profile Preview */}
                <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
                  {userProfile.photoURL ? (
                    <img 
                      src={userProfile.photoURL} 
                      alt={displayName} 
                      className="w-14 h-14 rounded-full object-cover"
                      style={{
                        objectPosition: userProfile.photoPosition 
                          ? `${userProfile.photoPosition.x}% ${userProfile.photoPosition.y}%` 
                          : 'center'
                      }}
                    />
                  ) : (
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center text-xl text-white font-bold"
                      style={{ background: `linear-gradient(135deg, ${accentColor}, #0A2342)` }}
                    >
                      {displayName.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{displayName}</h3>
                    <p className="text-sm text-gray-500">{userProfile.role}</p>
                  </div>
                </div>

                <h4 className="font-medium text-gray-900 mb-3">Stuur een connectieverzoek</h4>
                
                {/* Template Messages */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {templateMessages.map((msg, i) => (
                    <button
                      key={i}
                      onClick={() => setConnectMessage(msg)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        connectMessage === msg 
                          ? 'bg-[#4F6660] text-white border-[#4F6660]' 
                          : 'border-gray-200 text-gray-600 hover:border-[#4F6660] hover:text-[#4F6660]'
                      }`}
                    >
                      {msg.slice(0, 30)}...
                    </button>
                  ))}
                </div>

                {/* Message Input */}
                <textarea
                  value={connectMessage}
                  onChange={(e) => setConnectMessage(e.target.value)}
                  placeholder="Schrijf een persoonlijk bericht (optioneel)..."
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F6660]/20 focus:border-[#4F6660]"
                  rows={3}
                />

                {/* Actions */}
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      setShowConnectModal(false);
                      setConnectMessage('');
                    }}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleSendConnectionRequest}
                    disabled={sendingRequest}
                    className="flex-1 py-2.5 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                  >
                    {sendingRequest ? 'Versturen...' : 'Verstuur Verzoek'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>,
        document.body
      )}

      {/* RECOMMEND MODAL */}
      {showRecommendModal && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRecommendModal(false);
              setRecommendMessage('');
              setSelectedRecommendTo('');
              setRecommendationSent(false);
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {recommendationSent ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">⭐</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aanbeveling Verzonden!</h3>
                <p className="text-gray-600">
                  {postToNewsfeed && 'Je aanbeveling is gedeeld in de newsfeed.'}
                  {selectedRecommendTo && postToNewsfeed && ' '}
                  {selectedRecommendTo && `${userConnections.find(c => c.id === selectedRecommendTo)?.name} heeft een notificatie ontvangen.`}
                </p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-2xl">⭐</span>
                  <h3 className="text-lg font-semibold text-gray-900">Aanbevelen</h3>
                </div>

                {/* Profile being recommended */}
                <div className="flex items-center gap-4 mb-6 p-4 bg-orange-50 rounded-xl border border-orange-100">
                  {effectiveProfile?.photoURL ? (
                    <img src={effectiveProfile.photoURL} alt={effectiveProfile.fullName} className="w-14 h-14 rounded-full object-cover ring-2 ring-orange-200" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-orange-600 flex items-center justify-center text-xl text-white font-bold">
                      {effectiveProfile?.fullName?.charAt(0) || '?'}
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-amber-600 font-medium mb-1">Je beveelt aan:</p>
                    <h4 className="font-semibold text-gray-900">{effectiveProfile?.fullName}</h4>
                    <p className="text-sm text-gray-500">{effectiveProfile?.role || effectiveProfile?.company}</p>
                  </div>
                </div>

                {/* Recommend to specific person */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Aanbevelen aan (optioneel)
                  </label>
                  {loadingConnections ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500"></div>
                    </div>
                  ) : userConnections.length > 0 ? (
                    <select
                      value={selectedRecommendTo}
                      onChange={(e) => setSelectedRecommendTo(e.target.value)}
                      className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                    >
                      <option value="">-- Selecteer een connectie --</option>
                      {userConnections.map((conn) => (
                        <option key={conn.id} value={conn.id}>
                          {conn.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Je hebt nog geen connecties om aan te bevelen.</p>
                  )}
                </div>

                {/* Post to newsfeed checkbox */}
                <label className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={postToNewsfeed}
                    onChange={(e) => setPostToNewsfeed(e.target.checked)}
                    className="w-5 h-5 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Deel in newsfeed</span>
                    <p className="text-xs text-gray-500">Laat je netwerk deze aanbeveling zien</p>
                  </div>
                </label>

                {/* Template messages */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Snelle berichten
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {recommendTemplates.map((msg, i) => (
                      <button
                        key={i}
                        onClick={() => setRecommendMessage(msg)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          recommendMessage === msg 
                            ? 'bg-amber-500 text-white border-amber-500' 
                            : 'border-gray-200 text-gray-600 hover:border-amber-500 hover:text-amber-600'
                        }`}
                      >
                        {msg.slice(0, 25)}...
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom message */}
                <textarea
                  value={recommendMessage}
                  onChange={(e) => setRecommendMessage(e.target.value)}
                  placeholder="Schrijf een persoonlijke aanbeveling..."
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  rows={3}
                />

                {/* Actions */}
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => {
                      setShowRecommendModal(false);
                      setRecommendMessage('');
                      setSelectedRecommendTo('');
                    }}
                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleSendRecommendation}
                    disabled={sendingRecommendation || (!selectedRecommendTo && !postToNewsfeed)}
                    className="flex-1 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingRecommendation ? 'Versturen...' : '⭐ Aanbevelen'}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>,
        document.body
      )}

      {/* ADD EXPERTISE/INTERESSE MODAL */}
      {showAddExpertise && createPortal(
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddExpertise(false);
              setNewExpertiseValue('');
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Toevoegen</h3>
            
            <input
              type="text"
              value={newExpertiseValue}
              onChange={(e) => setNewExpertiseValue(e.target.value)}
              placeholder="Bijv. Marketing, Fotografie, React..."
              className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4F6660]/20 focus:border-[#4F6660] mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newExpertiseValue.trim()) {
                  addExpertiseItem('expertise');
                }
              }}
            />

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => addExpertiseItem('expertise')}
                disabled={!newExpertiseValue.trim() || saving}
                className="flex-1 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                🎯 Als Expertise
              </button>
              <button
                onClick={() => addExpertiseItem('interests')}
                disabled={!newExpertiseValue.trim() || saving}
                className="flex-1 py-2 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50"
              >
                ❤️ Als Interesse
              </button>
            </div>

            <button
              onClick={() => {
                setShowAddExpertise(false);
                setNewExpertiseValue('');
              }}
              className="w-full py-2 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Annuleren
            </button>
          </motion.div>
        </div>,
        document.body
      )}

      {/* Image Positioner Modal */}
      {showImagePositioner && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-lg w-full"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">📐 Positioneer je foto</h3>
            <p className="text-sm text-gray-600 mb-4">
              Sleep de foto om de beste positie te vinden voor de circulaire crop
            </p>
            
            {/* Image Positioner */}
            <div className="mb-6">
              <div 
                className="relative w-full aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-move select-none"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <img
                  src={tempPhotoURL}
                  alt="Position"
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                  }}
                  draggable={false}
                />
                {/* Circle Overlay to show crop area */}
                <div className="absolute inset-0 pointer-events-none">
                  <svg width="100%" height="100%" className="absolute inset-0">
                    <defs>
                      <mask id="circleMaskVerhaal">
                        <rect width="100%" height="100%" fill="white" opacity="0.7"/>
                        <circle cx="50%" cy="50%" r="40%" fill="black"/>
                      </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="black" opacity="0.5" mask="url(#circleMaskVerhaal)"/>
                    <circle cx="50%" cy="50%" r="40%" fill="none" stroke="white" strokeWidth="2" strokeDasharray="5,5"/>
                  </svg>
                </div>
                {/* Center crosshair */}
                <div 
                  className="absolute w-3 h-3 border-2 border-white rounded-full shadow-lg pointer-events-none"
                  style={{
                    left: `${tempPhotoPosition.x}%`,
                    top: `${tempPhotoPosition.y}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                />
              </div>
              
              {/* Preview */}
              <div className="mt-4 flex items-center justify-center space-x-4">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-2">Preview</p>
                  <div className="w-20 h-20 rounded-full bg-gray-200 overflow-hidden">
                    <img
                      src={tempPhotoURL}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      style={{
                        objectPosition: `${tempPhotoPosition.x}% ${tempPhotoPosition.y}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowImagePositioner(false);
                  URL.revokeObjectURL(tempPhotoURL);
                  setTempPhotoFile(null);
                  setTempPhotoURL('');
                }}
                disabled={uploadingPhoto}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Annuleren
              </button>
              <button
                type="button"
                onClick={() => setTempPhotoPosition({ x: 50, y: 50 })}
                disabled={uploadingPhoto}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ↺ Reset
              </button>
              <button
                type="button"
                onClick={handleImagePositionSave}
                disabled={uploadingPhoto}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingPhoto ? 'Opslaan...' : '✓ Opslaan'}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ProfileSliderHomeFromVerhaal;

